import { useApolloClient } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import { CCHelpers } from "@bigspring/core-components";
import { GlobalStorage } from "@bigspring/core-components/src/utils/global-storage";
import { getRoute } from "@config/routes";
import {
  AuthenticationSource,
  MeQuery,
  Role,
  useAuthLogoutMutation,
  MeDocument,
  useMeQuery,
  useIsNewAuthLazyQuery,
} from "@gql/generated/graphql";
import { googleLogout } from "@react-oauth/google";
import { LoadingContainer } from "@shared/Loading";
import { LoadingWrapper } from "@shared/Loading/styles";
import {
  PropsWithChildren,
  useCallback,
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  destroyCompanyIds,
  getCompanyId,
  storeCompanyId,
  storeCompanyIds,
} from "utils/auth";
import { useCustomRouter } from "../CustomRouter";
import { usePostAuthLogout } from "@rest/generated/auth/auth/auth";
import { getRuntimeConfig } from "lib/runtimeEnvClient";

/**
 * Possible values for User
 *
 * - Me_me: logged user info
 * - null: we know there is no user/session (i.e. token invalid or no token at all)
 * - undefined: we don't know yet (i.e. still loading) -- see useEffect under AuthenticationProvider component
 */
export type User = MeQuery["me"] | null;

interface IAuthentication {
  user?: User;
  isNewAuth: boolean;
  currentTab: string | null;
  setCurrentTab: (tab: string) => void;
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

const Context = createContext<IAuthentication>({
  user: undefined,
  isNewAuth: false,
  currentTab: null,
  setCurrentTab: () => null,
  login: async () => undefined,
  logout: async () => undefined,
});

/**
 * Only event reported without AdminTracker because its creation is before AdminTracker
 * @param me
 * @param apolloClient
 */
function callAnalyticsToReportSignIn(me: NonNullable<MeQuery["me"]>): void {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const analyticsClient = CCHelpers.useSegment(
    !!getRuntimeConfig("NEXT_PUBLIC_SEGMENT_ENABLED")
  );

  let event;
  if (me.isSuperAdmin) {
    event = {
      companyId: null,
      companyExternalId: null,
      profileRoles: Role.SuperAdmin,
    };
  } else {
    event = {
      companyId: me.companyProfiles[0].company.id,
      companyName: me.companyProfiles[0].company.name,
      profileRoles: me.companyProfiles[0].roles,
    };
  }
  void analyticsClient.track("User Logged In", { ...event });
}

export function AuthenticationProvider({
  children,
}: PropsWithChildren<unknown>) {
  const { replace, pathname } = useCustomRouter();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  const [currentTab, setCurrentTab] = useState<string | null>(null);
  const [isNewAuth, setNewAuth] = useState<boolean>(false);

  const {
    loading,
    data: user,
    refetch,
  } = useMeQuery({
    variables: CCHelpers.getTraits("admin"),
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const [getIsNewAuth] = useIsNewAuthLazyQuery();

  useEffect(() => {
    const email = user?.me?.companyProfiles?.find(
      (companyProfile) => companyProfile?.emailAddress
    )?.emailAddress;
    void getIsNewAuth({
      variables: {
        email: email as string,
      },
    }).then(({ data }) => {
      setNewAuth(!!data?.isNewAuth);
    });
  }, [user?.me?.companyProfiles]);

  const login = useCallback(async () => {
    void apolloClient
      .query({
        query: MeDocument,
        variables: CCHelpers.getTraits("admin"),
        fetchPolicy: "network-only",
        context: { queryDeduplication: false },
      })
      .then(({ data }) => {
        const me = data.me as MeQuery["me"];

        if (me?.companyProfiles?.[0]?.company?.id) {
          callAnalyticsToReportSignIn(me);

          if (!getCompanyId()) {
            const rolesWithAdminAccess = [
              Role.Coach,
              Role.CompanyAdministrator,
              Role.CompanyAnalytics,
              Role.ContentCreator,
              Role.SuperAdmin,
            ];

            const firstProfileWithAccess = me.companyProfiles?.find(
              (companyProfile) =>
                companyProfile?.roles?.some((role) =>
                  rolesWithAdminAccess.includes(role)
                )
            );

            const companyId =
              firstProfileWithAccess?.company?.id ??
              me.companyProfiles[0].company.id;
            storeCompanyId(companyId);
          }

          if (me.isSuperAdmin) {
            storeCompanyIds("ALL");
          } else {
            storeCompanyIds(
              me.companyProfiles.map(
                (companyProfile) => companyProfile.company.id
              )
            );
          }
        }
      })
      .finally(async () => {
        await replace("dashboard");
      });
  }, [apolloClient, refetch, replace]);

  const [doLogout] = useAuthLogoutMutation({
    variables: {
      logoutInput: {
        source: AuthenticationSource.Admin,
      },
    },
  });

  const { mutateAsync: authLogoutRequest } = usePostAuthLogout();

  const logout = useCallback(async () => {
    try {
      if (isNewAuth) {
        // await fetch("/api/cleanup-old-token", {
        //   method: "POST",
        // });

        await authLogoutRequest({ data: { source: "EDASH" } });
      } else {
        await doLogout();
      }
    } catch (e) {
      // we don't care if logout fails as we will also remove our credentials locally
    } finally {
      const store = new GlobalStorage();
      const isGoogleAuth = store.getItem("isGauth-admin") === "true";
      if (isGoogleAuth) googleLogout();

      store.clear();
      destroyCompanyIds();

      if (isNewAuth) {
        queryClient.clear();
      } else {
        await apolloClient.clearStore();
        await apolloClient.cache.reset({ discardWatches: true });
      }

      await replace("login");
    }
  }, [apolloClient, doLogout, replace, isNewAuth]);

  const value = useMemo(
    () => ({
      user: user?.me,
      isNewAuth: isNewAuth,
      currentTab,
      setCurrentTab,
      login,
      logout,
    }),
    [currentTab, login, logout, user]
  );

  if (loading && !user && pathname !== getRoute("login")) {
    return (
      <LoadingWrapper>
        <LoadingContainer />
      </LoadingWrapper>
    );
  }

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAuth() {
  return useContext(Context);
}
